"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface GavelIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface GavelIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const GavelIcon = forwardRef<GavelIconHandle, GavelIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const svgVariants: Variants = {
      normal: { rotate: 0 },
      animate: {
        rotate: [0, -15, 10, -5, 0],
        transition: { duration: 0.6 * duration, ease: "easeInOut", repeat: 0 },
      },
    };

    const pathVariants: Variants = {
      normal: { pathLength: 1, opacity: 1 },
      animate: {
        pathLength: [0, 1],
        opacity: [0.6, 1],
        transition: { duration: 0.8 * duration, ease: "easeInOut", repeat: 0 },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
        animate={controls}
        initial="normal"
        variants={svgVariants}
      >
        {/* Gavel handle + head */}
        <motion.path d="m14 13-8.381 8.38a1 1 0 0 1-3.001-3l8.384-8.381" variants={pathVariants} />
        {/* Strike line */}
        <motion.path d="m16 16 6-6" variants={pathVariants} />
        {/* Mallet diagonal */}
        <motion.path d="m21.5 10.5-8-8" variants={pathVariants} />
        {/* Handle bar */}
        <motion.path d="m8 8 6-6" variants={pathVariants} />
        {/* Cross */}
        <motion.path d="m8.5 7.5 8 8" variants={pathVariants} />
      </motion.svg>
    );
  },
);

GavelIcon.displayName = "GavelIcon";
export { GavelIcon };
